import { updateKeyInStore } from 'App';
import {
  expandSubTab,
  updateQuestionnaireRoutesWithQuestionnaireState,
} from 'app/features/questionnaires/edit/routes/slice';
import { store } from 'app/redux/store';
import { countries } from 'countries';
import * as Yup from 'yup';
import { requiredFiles } from '../../../useValidateQuestionnaire';
import {
  FamilyStateValues,
  TSpouseCitizenshipsRow,
  updateFieldArrayKey,
  updateKey,
} from './slice';

export const fields: IBaseField<
  keyof FamilyStateValues | keyof TSpouseCitizenshipsRow
>[] = [
  {
    type: 'select',
    name: 'fillingWithSpouse',
    label: 'Are you filing jointly with your spouse?',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'Not Sure', value: 'notSure' },
    ],
    effect: (updated) => {
      const isUpdated =
        store.getState().taxQuestionnaire.personal_details.the_basics.family
          .values.fillingWithSpouse !== updated.value;

      updateKeyInStore(updateKey)(updated);
      if (isUpdated) {
        store.dispatch(
          updateQuestionnaireRoutesWithQuestionnaireState({
            prepareQuestionnaire: store.getState().prepareQuestionnaire,
            taxQuestionnaire: store.getState().taxQuestionnaire,
          }),
        );

        // reopen current tab
        store.dispatch(expandSubTab('personal_details.the_basics'));
      }
    },
  },
  {
    type: 'text',
    name: 'spouseFirstName',
    label: 'Spouse First Name',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Spouse First Name',
  },
  {
    type: 'text',
    name: 'spouseMiddleName',
    label: 'Spouse Middle Name',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Spouse Middle Name',
  },
  {
    type: 'text',
    name: 'spouseLastName',
    label: 'Spouse Last Name',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Spouse Last Name',
  },
  {
    type: 'date',
    name: 'spouseDateOfBirth',
    label: 'Spouse Date of Birth',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Select Date',
  },
  {
    type: 'text',
    name: 'spouseOccupation',
    label: 'Spouse Occupation',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Spouse Occupation',
  },
  {
    type: 'radio',
    name: 'isSpouseUsCitizen',
    label: 'Is your spouse a US citizen?',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'radio',
        name: 'isSpouseGcHolder',
        label: 'Is your spouse a Green Card holder?',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'no',
        dependencies: [
          {
            type: 'checkbox',
            name: 'confirmSpouseSeperateUsTaxReturn',
            label:
              'I confirm that I understand that my spouse has to file separate US tax return',
            tooltip: '',
            info: [
              'Please note - if you elect to file separately, your spouse is obligated to file separate US tax return',
            ],
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'no',
          },
        ],
      },
      {
        type: 'radio',
        name: 'didSpouseRenounceGcDuringYear',
        label: 'Did your spouse obtain/renounce a green card during {year}?',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'no',
        dependencies: [
          {
            type: 'upload',
            name: 'GcCopy',
            label:
              'Please upload a scanned copy of the front of the green card showing the date of issuance',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            predicate: (value) => value === 'yes',
          },
        ],
      },
    ],
  },
  {
    type: 'radio',
    name: 'spouseHasSocialSecurityNum',
    label: 'Does your spouse have a Social Security Number or ITIN?',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'maskedInput',
        mask: '999-99-9999',
        name: 'spouseSocialSecurityNum',
        label: "Spouse's Social Security Number or ITIN",
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
      },
    ],
  },
  {
    type: 'fieldArray',
    name: 'spouseCitizenships',
    label: 'Citizenships List',
    effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
    dependencies: [
      {
        name: 'country',
        type: 'country',
        label: 'Please list any country citizenship(s) that your spouse holds',
        effect: updateKeyInStore(updateKey),
      },
    ],
    defaultDependency: { country: null },
  },

  {
    type: 'radio',
    name: 'marriedForPartOfFillingYear',
    label: 'Were you married for only part of the filing year?',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'textarea',
        name: 'marriedForPartOfFillingYearDetails',
        label:
          'Please provide details (ie how your marital status changed during the year)',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Enter',
      },
    ],
  },
  {
    type: 'radio',
    name: 'IrsSentIdentityProtection',
    label: 'Did the IRS send your spouse an Identity Protection PIN?',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        type: 'text',
        name: 'pleaseEnterPin',
        label: 'Please enter the PIN',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Please enter the PIN',
      },
    ],
  },
];

export const validationSchema = Yup.object().shape({
  spouseFirstName: Yup.string().required('required').nullable(),
  spouseMiddleName: Yup.string().required('required').nullable(),
  spouseLastName: Yup.string().required('required').nullable(),
  spouseDateOfBirth: Yup.string().required('required').nullable(),
  spouseOccupation: Yup.string().required('required').nullable(),
  spouseSocialSecurityNum: Yup.string()
    .nullable()
    .when('spouseHasSocialSecurityNum', {
      is: 'yes',
      then: Yup.string().nullable().required('required'),
    }),
});
